/**
 * NaamDarbar — Claim resume logic (web)
 *
 * Pure helper that decides WHERE a user should land when they (re)enter the
 * ragi profile-claim flow. Mirrors what the old mandatory OnboardingGate did
 * via force-redirects, but returns a target instead — so the claim is now
 * opt-in (driven by the dedicated /claim page, banners, and "Continue" CTAs)
 * rather than a hard gate.
 *
 * Resume step is fully derivable from the backend state already exposed by
 * GET /auth/me (profile + active_claim + has_initiated_claim) — no extra
 * "current_step" column is needed.
 */

import type { AppProfile, ActiveClaim } from '../context/AuthContext';

export type ClaimResumeTarget =
  | 'approved'      // already a verified raagi — edit profile
  | 'status'        // submitted / under_review / needs_more_info / rejected
  | 'identity'      // draft: artist already chosen — verify phone + submit
  | 'find-profile'; // no claim yet — start fresh

const STATUS_PAGE_STATUSES = new Set([
  'submitted', 'under_review', 'needs_more_info', 'rejected',
]);

/**
 * Decide the resume target for the claim flow from current auth state.
 */
export function claimResume(
  profile: AppProfile | null,
  activeClaim: ActiveClaim | null,
): ClaimResumeTarget {
  // Approved raagi — they manage their profile, not the claim wizard.
  if (profile?.role === 'raagi') return 'approved';

  const status = activeClaim?.status;

  if (status && STATUS_PAGE_STATUSES.has(status)) return 'status';

  // Draft: an artist has already been selected (a draft row always has an
  // artist_id) — the next actionable step is identity (phone verify + submit).
  if (status === 'draft') return 'identity';

  // No claim at all — start by finding the profile.
  return 'find-profile';
}

/**
 * Map a resume target to a concrete web route.
 */
export function claimResumeRoute(target: ClaimResumeTarget): string {
  switch (target) {
    case 'approved':     return '/profile/ragi';
    case 'status':       return '/claim/status';
    case 'identity':     return '/signup/identity?track=raagi';
    case 'find-profile': return '/signup/find-profile?track=raagi';
  }
}

// ── Exact-step persistence ───────────────────────────────────────────
// A `draft` claim can't tell us which wizard sub-step the user was on
// (find-profile vs claim-or-create vs identity). When the user Skips, we
// remember the exact step so "Continue" returns them to the same page.

const LAST_STEP_KEY = 'nd:claim-last-step';
const RESUMABLE_STEPS = ['find-profile', 'claim-or-create', 'identity'];

export function saveClaimStep(step: string | undefined): void {
  if (step && RESUMABLE_STEPS.includes(step)) localStorage.setItem(LAST_STEP_KEY, step);
}

export function clearClaimStep(): void {
  localStorage.removeItem(LAST_STEP_KEY);
}

/**
 * The concrete route to resume the claim at — prefers the exact step the user
 * skipped from (when the claim is still in progress), else the derived step.
 */
export function claimResumeRouteFor(
  profile: AppProfile | null,
  activeClaim: ActiveClaim | null,
): string {
  const target = claimResume(profile, activeClaim);
  // Only honour the saved step while the claim is still being filled in.
  if (target === 'identity' || target === 'find-profile') {
    const saved = localStorage.getItem(LAST_STEP_KEY);
    if (saved && RESUMABLE_STEPS.includes(saved)) {
      return `/signup/${saved}?track=raagi`;
    }
  }
  return claimResumeRoute(target);
}
